/**
 * Block Catalog — canonical metadata for every BehaviorGraph node type.
 *
 * Single source of truth for:
 * - the documentation generator (`tools/generate-docs.ts`)
 * - editor palettes (sandbox, TraceFrontend) that need socket/config metadata
 * - the AI prompt that teaches the assistant what each node does
 *
 * Each entry pairs a runtime `GraphOp` with the public-facing metadata an
 * editor or doc consumer needs: a label, a category, flow/data sockets, and
 * static configuration fields. The JSDoc on each `@block` constant is the
 * human description — the generator extracts it verbatim.
 *
 * UI-only concerns (palette colors, icon assignments, rendering hints) stay
 * out of this file. Consumers layer those on top.
 */

import { GraphOps, type GraphOp } from '../../scenedata/behaviors/behaviorgraphdata';
import { INTERVAL_MACRO_OP, TOGGLE_ACTIVE_MACRO_OP } from './macros';

// ============================================================================
// Types
// ============================================================================

/**
 * Socket type. Mirrors `TypeSignature` from `behaviorgraphdata.ts` plus an
 * `'any'` escape hatch for blocks like `math/eq` and `math/select` that
 * accept heterogeneous inputs.
 */
export type SocketType =
    | 'bool' | 'int' | 'float'
    | 'float2' | 'float3' | 'float4'
    | 'color' | 'string' | 'any';

export type BlockCategory = 'event' | 'execution' | 'data' | 'subgraph';

/**
 * Registration source — where the runtime implementation comes from.
 * Drives docs to flag "standard KHR" vs "Trace extension" blocks.
 */
export type BlockRegistration =
    /** Built into Babylon's KHR_interactivity loader (math/*, flow/*, variable/*, event/onStart, event/onTick). */
    | 'babylon-khr'
    /** Built into Babylon under another KHR extension (KHR_node_selectability, KHR_node_hoverability). */
    | 'babylon-khr-extension'
    /** Registered by `registerTraceBlocks()` under the TRACE_interactivity extension. */
    | 'trace-extension'
    /**
     * Canned composition — a logical editor block with NO runtime op.
     * Authoring it expands into standard KHR nodes tagged with macro metadata
     * (see macros/registry.ts); recognition collapses them back for the palette/canvas.
     * Portable to any conformant viewer by construction.
     */
    | 'macro';

/**
 * Pseudo-ops for macro catalog entries. Never appear in stored GraphData —
 * they name the logical block; the expansion emits only standard `GraphOps`.
 * High-level authoring may emit them as single nodes; `lowerMacros`
 * (macros/registry.ts) rewrites those into the canonical expansion at commit.
 */
export type MacroOp = typeof INTERVAL_MACRO_OP | typeof TOGGLE_ACTIVE_MACRO_OP;

export interface SocketDef {
    name: string;
    type: SocketType;
    /** Short description of what the socket carries. */
    description?: string;
}

export interface ConfigFieldDef {
    name: string;
    /**
     * Field type. `transformPath` is a TransformPath string (`"$self"`, `"./Child"`, etc.).
     * `transformProperty` is one of `"translation" | "rotation" | "scale"` (for pointer/* ops).
     */
    type: 'string' | 'number' | 'boolean' | 'transformPath' | 'transformProperty';
    label: string;
    default?: unknown;
    /** Short description of what this config controls. */
    description?: string;
}

export interface BlockDef {
    /**
     * Op string — e.g. `"trace/setActive"`. Must match a value in `GraphOps`,
     * except `registration: 'macro'` entries, which use a `macro/` pseudo-op.
     */
    op: GraphOp | MacroOp;
    /** Display name shown in the editor palette. */
    label: string;
    /** Grouping for editor palettes and docs. */
    category: BlockCategory;
    /** Where the runtime implementation lives (drives doc badging). */
    registration: BlockRegistration;
    /** Optional sub-grouping for doc headings (e.g. "Math", "Pages", "Media"). */
    docGroup: string;
    /** Description for docs and tooltips. Plain prose, no markdown. */
    description: string;
    /** Flow input sockets (execution nodes only). */
    flowInputs: string[];
    /** Flow output sockets. Event blocks fire these; execution blocks route to them. */
    flowOutputs: string[];
    /** Data input sockets. */
    dataInputs: SocketDef[];
    /** Data output sockets. */
    dataOutputs: SocketDef[];
    /** Static configuration fields (target paths, clip names, variable indices). */
    configFields: ConfigFieldDef[];
    /** When true, surfaced in docs/UI with a "deprecated" badge. */
    deprecated?: boolean;
    /** Free-form note rendered after the description (e.g. capability hints). */
    note?: string;
}

// ============================================================================
// Helpers — keep entries terse
// ============================================================================

const TARGET_FIELD: ConfigFieldDef = {
    name: 'target',
    type: 'transformPath',
    label: 'Target',
    default: '',
    description: 'TransformPath to the affected scene object.',
};

const PARENT_FIELD: ConfigFieldDef = {
    ...TARGET_FIELD,
    label: 'Parent',
    description: 'TransformPath to the parent whose children are addressed.',
};

const FLOW_IN: string[] = ['in'];
const FLOW_OUT: string[] = ['out'];

// ============================================================================
// Catalog
// ============================================================================

export const BLOCK_DEFS: BlockDef[] = [
    // ── Events ─────────────────────────────────────────────────────────────
    {
        op: GraphOps.EVENT_ON_START,
        label: 'On Start',
        category: 'event',
        registration: 'babylon-khr',
        docGroup: 'Lifecycle',
        description: 'Fires once when the scene enters play mode.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [],
    },
    {
        op: GraphOps.EVENT_ON_TICK,
        label: 'On Tick',
        category: 'event',
        registration: 'babylon-khr',
        docGroup: 'Lifecycle',
        description: 'Fires every frame while the scene is playing.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [
            { name: 'timeSinceStart', type: 'float', description: 'Seconds since the scene started.' },
            { name: 'deltaTime', type: 'float', description: 'Seconds since the previous tick.' },
        ],
        configFields: [],
    },
    {
        op: GraphOps.EVENT_ON_SELECT,
        label: 'On Select',
        category: 'event',
        registration: 'babylon-khr-extension',
        docGroup: 'Pointer',
        description: 'Fires when the target is clicked or tapped.',
        note: 'Uses the KHR_node_selectability extension. The target must be selectable.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.EVENT_ON_HOVER_IN,
        label: 'On Hover In',
        category: 'event',
        registration: 'babylon-khr-extension',
        docGroup: 'Pointer',
        description: 'Fires when the pointer enters the target.',
        note: 'Uses the KHR_node_hoverability extension.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.EVENT_ON_HOVER_OUT,
        label: 'On Hover Out',
        category: 'event',
        registration: 'babylon-khr-extension',
        docGroup: 'Pointer',
        description: 'Fires when the pointer leaves the target.',
        note: 'Uses the KHR_node_hoverability extension.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_ON_PROXIMITY,
        label: 'On Proximity',
        category: 'event',
        registration: 'trace-extension',
        docGroup: 'Spatial',
        description: 'Fires when the camera enters or exits a radius around the target.',
        flowInputs: [],
        flowOutputs: ['enter', 'exit'],
        dataInputs: [
            { name: 'threshold', type: 'float', description: 'Radius in meters.' },
        ],
        dataOutputs: [
            { name: 'distance', type: 'float', description: 'Current distance from the camera to the target.' },
        ],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_ON_STATE_CHANGE,
        label: 'On State Change',
        category: 'event',
        registration: 'trace-extension',
        docGroup: 'States',
        description: "Fires when the target's Properties active state changes.",
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [
            { name: 'prevState', type: 'string', description: 'State name before the change (empty if cleared).' },
            { name: 'newState', type: 'string', description: 'State name after the change (empty if cleared).' },
        ],
        configFields: [TARGET_FIELD],
    },

    // ── Flow control ───────────────────────────────────────────────────────
    {
        op: GraphOps.FLOW_BRANCH,
        label: 'Branch',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Flow',
        description: 'Routes execution to one of two outputs based on a boolean condition.',
        flowInputs: FLOW_IN,
        flowOutputs: ['true', 'false'],
        dataInputs: [{ name: 'condition', type: 'bool' }],
        dataOutputs: [],
        configFields: [],
    },
    {
        op: GraphOps.FLOW_SEQUENCE,
        label: 'Sequence',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Flow',
        description: 'Runs each output flow in order. Use to fire multiple actions from one event.',
        note: 'The runtime supports any number of outputs (one per connected flow). The catalog lists the default two — add more by wiring additional `out_N` sockets.',
        flowInputs: FLOW_IN,
        flowOutputs: ['0', '1'],
        dataInputs: [],
        dataOutputs: [],
        configFields: [],
    },
    {
        op: GraphOps.FLOW_SET_DELAY,
        label: 'Delay',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Flow',
        description: 'Waits a number of seconds, then continues. Use to schedule a later action.',
        note: '`out` fires immediately; `done` fires after `duration` seconds. `cancel` aborts a pending delay. `err` fires if the duration is invalid (negative, NaN, or infinite).',
        flowInputs: ['in', 'cancel'],
        flowOutputs: ['out', 'done', 'err'],
        dataInputs: [{ name: 'duration', type: 'float', description: 'Delay in seconds before `done` fires.' }],
        dataOutputs: [{ name: 'lastDelay', type: 'int', description: 'Reference to the delay just scheduled. Feed into Cancel Delay to cancel this specific delay.' }],
        configFields: [],
    },
    {
        op: INTERVAL_MACRO_OP,
        label: 'Interval',
        category: 'execution',
        registration: 'macro',
        docGroup: 'Flow',
        description: 'Fires repeatedly, every `interval` seconds, until cancelled. Canned composition — expands to standard KHR nodes (Delay, Sequence, Branch, a private flag variable and a private custom event), portable to any conformant viewer, no custom op and no flow cycle.',
        note: 'Logical block backed by expansion (macros/interval.ts): the loop re-enters through a private internal custom event (`event/send` → `event/receive`), because the ratified KHR_interactivity spec forbids flow cycles. Fire-then-re-arm: each period the delay elapses, its event is delivered on the next frame (Babylon\'s KHR loader and the native runtime both defer custom events), the `done` chain fires, then the delay re-arms — so a period is `interval` plus one frame, the payload\'s synchronous cost and frame quantization, identical on every conformant client. `in` (re)starts: a pending cycle is cancelled first, then the delay arms; `out` continues immediately. `cancel` stops the loop exactly, even inside the one-frame delivery window (a private running flag gates the delivery). The delay reads `interval` each time it arms, so a re-trigger (or a wired value) applies the current value. Graph teardown stops it via the delay block\'s lifecycle. Authoring surfaces guard the period: `GraphBuilder.interval()` rejects non-positive/non-finite values, and lowering a `macro/interval` with an invalid literal expands with the loop unarmed (warns, never fires). A WIRED period cannot be checked at edit time: negative/NaN errs the delay at runtime, and a dynamic 0 degrades to once per frame. Recognition is metadata-based: editing the expansion away from its canonical wiring degrades it to plain nodes — they still execute, but no longer collapse to this block. Stored expansions in the pre-#896 cyclic shape are migrated when the graph is loaded.',
        flowInputs: ['in', 'cancel'],
        flowOutputs: ['out', 'done'],
        dataInputs: [{ name: 'interval', type: 'float', description: 'Seconds between firings.' }],
        dataOutputs: [],
        configFields: [],
    },
    {
        op: GraphOps.FLOW_CANCEL_DELAY,
        label: 'Cancel Delay',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Flow',
        description: 'Cancels a specific pending delay scheduled by a Delay node.',
        note: "Wire the source Delay node's `lastDelay` output into `delay` (usually stashed in a variable first). Alternatively, drive the Delay node's own `cancel` flow input to cancel all of that node's pending delays at once.",
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'delay', type: 'int', description: 'The lastDelay reference from the Delay node to cancel.' }],
        dataOutputs: [],
        configFields: [],
    },

    // ── Object activation ──────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_SET_ACTIVE,
        label: 'Set Active',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Object',
        description: 'Activate or deactivate a scene object. Inactive objects stop rendering and stop responding to input, along with their whole subtree. This is authored scene state, not glTF `KHR_node_visibility` (which is rendering only).',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'state', type: 'bool', description: 'true to show, false to hide.' }],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: TOGGLE_ACTIVE_MACRO_OP,
        label: 'Toggle Active',
        category: 'execution',
        registration: 'macro',
        docGroup: 'Object',
        description: 'Flip a scene object between active and inactive. Canned composition — expands to Get Active → Not → Set Active.',
        note: 'Logical block backed by expansion (macros/): reads the target\'s current active state, inverts it, and writes it back. Named for `active` rather than "visibility" because it toggles authored scene state — inactive objects stop rendering AND stop responding to input, subtree included — whereas glTF `KHR_node_visibility` is rendering only. Unlike Interval, this expansion uses TRACE_interactivity ops (`trace/getActive` / `trace/setActive`), since glTF has no standard pointer for this: it is portable across Trace clients rather than to every conformant viewer. Recognition is metadata-based: editing the expansion away from its canonical wiring degrades it to plain nodes — they still execute, but no longer collapse to this block.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [
            { ...TARGET_FIELD, description: 'TransformPath to the object whose active state is flipped.' },
        ],
    },
    {
        op: GraphOps.TRACE_SET_BEHAVIOR_ENABLED,
        label: 'Enable Behavior',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Object',
        description: "Switch a behavior's runtime effect on or off — e.g. start or stop Tethering/Facing.",
        note: 'Works on RuntimeToggleable behaviors (Billboard, Tether, ViewAnchor). The authored `enabled` field on the behavior is the state at every play-mode start; disabling a Billboard applies its onExit (ease back / hold), disabling a Tether or ViewAnchor stops it in place and re-enabling re-runs its delay. Other behaviors warn and no-op.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'enabled', type: 'bool', description: 'true to enable, false to disable. Defaults to true.' }],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'behavior', type: 'string', label: 'Behavior', default: '', description: 'Behavior type name on the target, e.g. "Tether" or "Billboard".' },
        ],
    },
    {
        op: GraphOps.TRACE_GET_ACTIVE,
        label: 'Get Active',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Object',
        description: 'Read whether a scene object is currently active.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_GET_CHILD_COUNT,
        label: 'Get Child Count',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Object',
        description: "Read the number of direct children under a parent.",
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [PARENT_FIELD],
    },
    {
        op: GraphOps.TRACE_SET_ACTIVE_CHILD,
        label: 'Set Active Child',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Object',
        description: 'Show one child of a parent and hide its siblings. Index is clamped to the child range.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'index', type: 'float', description: 'Zero-based index of the child to show.' }],
        dataOutputs: [],
        configFields: [PARENT_FIELD],
    },

    // ── States ─────────────────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_SET_STATE,
        label: 'Set State',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'States',
        description: "Set the active state on the target AND every participating descendant in its state group (scoped fan-out, pruned at nested stateGroup boundaries). Empty string clears the state (reverts to base). Targeting a group owner switches the whole group.",
        note: 'Scoped per STATE_SCOPE_SPEC.md — resolved live against the scene. Triggers any transitions defined on the Properties states; re-setting the current state is a no-op (R7).',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'state', type: 'string' }],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_GET_STATE,
        label: 'Get State',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'States',
        description: "Read the active state name from a target's Properties. Empty when no state is active.",
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'state', type: 'string' }],
        configFields: [TARGET_FIELD],
    },

    // ── Properties ─────────────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_GET_PROPERTY,
        label: 'Get Property',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Properties',
        description: "Read a named property value from a target's Properties behavior.",
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'any' }],
        configFields: [
            TARGET_FIELD,
            { name: 'property', type: 'string', label: 'Property', default: '', description: 'Property name as declared on the Properties behavior.' },
        ],
    },
    {
        op: GraphOps.TRACE_SET_PROPERTY,
        label: 'Set Property',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Properties',
        description: "Write a value to a named property on a target's Properties behavior. Animates when the declaration has a default transition.",
        note: "Optional `transition` config (`{ duration, easing }`) overrides the declaration's default transition for this edge; omitted → declaration default → snap.",
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'value', type: 'any' }],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'property', type: 'string', label: 'Property', default: '', description: 'Property name as declared on the Properties behavior.' },
        ],
    },

    // ── Time ───────────────────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_TIME,
        label: 'Time',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Lifecycle',
        description: 'Read play-mode elapsed time. Pull-evaluated — re-reads on every access.',
        note: 'Mirrors the `time.elapsed` system value available in `{expression}` bindings.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'elapsed', type: 'float', description: 'Seconds since play mode started (0 when not playing).' }],
        configFields: [],
    },

    // ── Transform (KHR pointer/) ───────────────────────────────────────────
    // `pointer/get` and `pointer/set` are standard KHR_interactivity ops, but
    // trace-viewer overrides the implementations (PointerGetBlock /
    // PointerSetBlock) to address targets via TransformPath instead of glTF
    // JSON pointer paths.
    {
        op: GraphOps.POINTER_SET,
        label: 'Set Transform',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Transform',
        description: 'Write a transform component on the target.',
        note: 'Value is a Vec3 for translation/scale or a quaternion [x, y, z, w] for rotation — typed `any` because the shape depends on the `property` config. Always a snap write; it cancels a running pointer/interpolate on the SAME pointer (target + property) only — other components’ interpolations continue (KHR rule). For animated writes use pointer/interpolate. Target resolution is strict, matching pointer/interpolate: an empty target means the host, but a non-empty path that does not resolve fires `err` instead of `out` rather than silently retargeting the host. An invalid value for the property fires `err` too, and nothing is written.',
        flowInputs: FLOW_IN,
        flowOutputs: ['out', 'err'],
        dataInputs: [{ name: 'value', type: 'any', description: 'Vec3 for translation/scale, Vec4 quaternion for rotation.' }],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'property', type: 'transformProperty', label: 'Property', default: 'translation', description: 'Which transform component to write.' },
        ],
    },
    {
        op: GraphOps.POINTER_INTERPOLATE,
        label: 'Interpolate Transform',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Transform',
        description: 'Animate a transform component on the target toward a value over a duration, easing along a cubic bezier.',
        note: 'This IS the KHR-standard pointer/interpolate op (Trace overrides the implementation for TransformPath addressing, like pointer/set). `p1`/`p2` are CSS cubic-bezier control points — every Trace easing is one of these (see EASING_CONTROL_POINTS); missing → easeInOut. A new interpolation on the same pointer (target + property) REPLACES the running one and cancels its pending `done`; a pointer/set snap on the same pointer also cancels it. `duration` <= 0 → snap, `done` fires immediately after `out`. `err` fires instead of `out` when the target/property/value is invalid.',
        flowInputs: FLOW_IN,
        flowOutputs: ['out', 'done', 'err'],
        dataInputs: [
            { name: 'value', type: 'any', description: 'Vec3 for translation/scale, Vec4 quaternion for rotation.' },
            { name: 'duration', type: 'float', description: 'Interpolation length in seconds. <= 0 snaps.' },
            { name: 'p1', type: 'float2', description: 'First cubic-bezier control point [x1, y1].' },
            { name: 'p2', type: 'float2', description: 'Second cubic-bezier control point [x2, y2].' },
        ],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'property', type: 'transformProperty', label: 'Property', default: 'translation', description: 'Which transform component to animate.' },
        ],
    },
    {
        op: GraphOps.POINTER_GET,
        label: 'Get Transform',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Transform',
        description: 'Read a transform component from the target.',
        note: 'Output is a Vec3 for translation/scale or a quaternion [x, y, z, w] for rotation — typed `any` because the shape depends on the `property` config.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'any' }],
        configFields: [
            TARGET_FIELD,
            { name: 'property', type: 'transformProperty', label: 'Property', default: 'translation' },
        ],
    },

    // ── Animation ──────────────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_PLAY_ANIMATION,
        label: 'Play Animation',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Animation',
        description: 'Play an animation clip on a target with an Animation behavior.',
        note: '`out` fires immediately; `done` fires when the clip finishes (never, if looping).',
        flowInputs: FLOW_IN,
        flowOutputs: ['out', 'done'],
        dataInputs: [
            { name: 'speed', type: 'float', description: 'Playback rate. 1 = normal, negative = reverse.' },
            { name: 'loop', type: 'bool' },
        ],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'clip', type: 'string', label: 'Clip', default: '', description: 'Animation clip name. Empty plays the default clip.' },
        ],
    },
    {
        op: GraphOps.TRACE_PLAY_ANIMATION_EXTERNAL,
        label: 'Play External Animation',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Animation',
        description: 'Play an animation clip sourced from an external GLB.',
        flowInputs: FLOW_IN,
        flowOutputs: ['out', 'started', 'done'],
        dataInputs: [
            { name: 'speed', type: 'float' },
            { name: 'loop', type: 'bool' },
        ],
        dataOutputs: [],
        configFields: [
            TARGET_FIELD,
            { name: 'src', type: 'string', label: 'Source GLB', default: '', description: 'URL of the GLB containing the clip.' },
            { name: 'clip', type: 'string', label: 'Clip', default: '' },
        ],
    },

    // ── Media (Playable: Video, AudioClip, Animation, Pages) ───────────────
    {
        op: GraphOps.TRACE_PLAY_MEDIA,
        label: 'Play Media',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Play or resume media on any Playable target (Video, AudioClip, Animation, Pages).',
        note: '`src` is only honored on Video and AudioClip (they swap the URL before playback). `volume` is only honored on volume-capable targets.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [
            { name: 'src', type: 'string', description: 'Optional URL swap (Video / AudioClip only).' },
            { name: 'volume', type: 'float', description: '0–1 (only honored on volume-capable targets).' },
            { name: 'seek', type: 'float', description: 'Seek to seconds before playing. Negative = no seek.' },
        ],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_PAUSE_MEDIA,
        label: 'Pause Media',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Pause media at the current position.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_STOP_MEDIA,
        label: 'Stop Media',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Stop media and reset to time 0.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_SEEK_MEDIA,
        label: 'Seek Media',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Seek to a specific time without changing play state.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'time', type: 'float', description: 'Seconds from the start of the media.' }],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_SET_MEDIA_VOLUME,
        label: 'Set Media Volume',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Set the volume on a Video or AudioClip target.',
        note: 'No-op on targets that do not implement PlayableVolume (Animation, Pages).',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'volume', type: 'float', description: 'Volume in the range 0–1.' }],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_IS_MEDIA_PLAYING,
        label: 'Is Media Playing',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: "Whether the target's media is currently playing (pull-evaluated).",
        note: 'True while playback runs on the resolved Playable (Video, AudioClip, Animation…). False for unresolvable targets and Playables without playback state (Pages). Feed into flow/branch for a Play/Pause toggle that stays true to reality — including media that ended on its own.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_GET_MEDIA_VOLUME,
        label: 'Get Media Volume',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: "Read the target's current volume, 0–1 (pull-evaluated).",
        note: 'Reads the underlying media element (Video, AudioClip). Returns 1 — the element default — for targets without a volume control, so "no volume" reads as unmuted.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.TRACE_PLAY_VIDEO,
        label: 'Play Video',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Media',
        description: 'Legacy video playback. Prefer Play Media for new graphs.',
        deprecated: true,
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [
            { name: 'play', type: 'bool' },
            { name: 'volume', type: 'float' },
            { name: 'seek', type: 'float' },
        ],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },

    // ── Pages ──────────────────────────────────────────────────────────────
    {
        op: GraphOps.PAGES_NEXT,
        label: 'Pages Next',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Pages',
        description: 'Advance to the next page on a Pages behavior.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [
            { name: 'wrap', type: 'bool', description: 'When true, wraps from the last page back to the first.' },
            { name: 'animate', type: 'bool' },
        ],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.PAGES_PREV,
        label: 'Pages Prev',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Pages',
        description: 'Go back one page on a Pages behavior.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [
            { name: 'wrap', type: 'bool' },
            { name: 'animate', type: 'bool' },
        ],
        dataOutputs: [],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.PAGES_GET_ACTIVE_INDEX,
        label: 'Get Page Index',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Pages',
        description: 'Read the zero-based index of the currently active page.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [TARGET_FIELD],
    },
    {
        op: GraphOps.PAGES_GET_PAGE_COUNT,
        label: 'Get Page Count',
        category: 'data',
        registration: 'trace-extension',
        docGroup: 'Pages',
        description: 'Read the total number of pages.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [TARGET_FIELD],
    },

    // ── Navigation ─────────────────────────────────────────────────────────
    {
        op: GraphOps.TRACE_NAVIGATE,
        label: 'Navigate',
        category: 'execution',
        registration: 'trace-extension',
        docGroup: 'Navigation',
        description: 'Open a URL.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'url', type: 'string' }],
        dataOutputs: [],
        configFields: [],
    },

    {
        op: GraphOps.MATH_MIN, label: 'Minimum', category: 'data',
        registration: 'babylon-khr', docGroup: 'Math', description: 'The smaller of a and b.',
        flowInputs: [], flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }], configFields: [],
    },
    {
        op: GraphOps.MATH_MAX, label: 'Maximum', category: 'data',
        registration: 'babylon-khr', docGroup: 'Math', description: 'The larger of a and b.',
        flowInputs: [], flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }], configFields: [],
    },
    {
        op: GraphOps.MATH_POW, label: 'Power', category: 'data',
        registration: 'babylon-khr', docGroup: 'Math', description: 'a raised to the power b.',
        flowInputs: [], flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }], configFields: [],
    },
    {
        op: GraphOps.MATH_LE, label: 'Less Than or Equal', category: 'data',
        registration: 'babylon-khr', docGroup: 'Math', description: 'True when a is less than or equal to b.',
        flowInputs: [], flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'bool' }], configFields: [],
    },
    // ── Math: arithmetic ───────────────────────────────────────────────────
    {
        op: GraphOps.MATH_ADD,
        label: 'Add',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a + b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_SUB,
        label: 'Subtract',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a − b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_MUL,
        label: 'Multiply',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a × b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_DIV,
        label: 'Divide',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a ÷ b. Result is undefined when b is zero.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_FLOOR,
        label: 'Floor',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'Largest integer ≤ a.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_RANDOM,
        label: 'Random',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'Random float in [min, max).',
        note: 'Re-evaluates every time the output is pulled — chain through `variable/set` if you need a stable value across a frame.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'min', type: 'float' }, { name: 'max', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float' }],
        configFields: [],
    },

    // ── Math: comparison ───────────────────────────────────────────────────
    {
        op: GraphOps.MATH_EQ,
        label: 'Equal',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a equals b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'any' }, { name: 'b', type: 'any' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_GT,
        label: 'Greater Than',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a > b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_GE,
        label: 'Greater or Equal',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a ≥ b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_LT,
        label: 'Less Than',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a < b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },

    // ── Math: boolean ──────────────────────────────────────────────────────
    {
        op: GraphOps.MATH_NOT,
        label: 'NOT',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'Boolean negation.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'bool' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_AND,
        label: 'AND',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a AND b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'bool' }, { name: 'b', type: 'bool' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_OR,
        label: 'OR',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'a OR b.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'bool' }, { name: 'b', type: 'bool' }],
        dataOutputs: [{ name: 'value', type: 'bool' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_SELECT,
        label: 'Select',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'Returns a when condition is true, b otherwise.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [
            { name: 'condition', type: 'bool' },
            { name: 'a', type: 'any' },
            { name: 'b', type: 'any' },
        ],
        dataOutputs: [{ name: 'value', type: 'any' }],
        configFields: [],
    },
    {
        op: GraphOps.MATH_COMBINE3,
        label: 'Combine Vec3',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Math',
        description: 'Pack three floats into a Vec3.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [{ name: 'a', type: 'float' }, { name: 'b', type: 'float' }, { name: 'c', type: 'float' }],
        dataOutputs: [{ name: 'value', type: 'float3' }],
        configFields: [],
    },

    // ── Variables ──────────────────────────────────────────────────────────
    {
        op: GraphOps.VARIABLE_GET,
        label: 'Get Variable',
        category: 'data',
        registration: 'babylon-khr',
        docGroup: 'Variables',
        description: 'Read a graph-scoped variable. Resolves by index — see the Variables panel.',
        note: 'Socket type is `any` because the actual type comes from `GraphData.variables[index].type` (bool, float, float3, etc.). Pull evaluation: this re-reads on every access — if a downstream chain writes via `variable/set` and then reads back, you may see the already-updated value.',
        flowInputs: [],
        flowOutputs: [],
        dataInputs: [],
        dataOutputs: [{ name: 'value', type: 'any' }],
        configFields: [
            { name: 'variable', type: 'number', label: 'Variable Index', default: 0, description: 'Index into the graph variables[] array.' },
        ],
    },
    {
        op: GraphOps.VARIABLE_SET,
        label: 'Set Variable',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Variables',
        description: 'Write a graph-scoped variable.',
        note: 'Socket type is `any` because the actual type comes from `GraphData.variables[index].type`.',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [{ name: 'value', type: 'any' }],
        dataOutputs: [],
        configFields: [
            { name: 'variable', type: 'number', label: 'Variable Index', default: 0 },
        ],
    },
];

/**
 * Standard ops the runtime executes and the catalog describes, but which are
 * deliberately NOT in `BLOCK_DEFS` — so they are not offered in editor
 * palettes, docs pages or the AI prompt.
 *
 * Custom events live here: a send/receive pair only means something together
 * with an entry in `GraphData.events`, and no editor defines those yet. They
 * reach stored graphs through macro expansions (the Interval re-enters its
 * loop through an internal event, #896) and KHR imports. `getBlockDef`
 * resolves them, so validation, compilation and canvas rendering of an
 * un-collapsed expansion all work.
 *
 * Sockets: the catalog lists only the fixed ones. The spec gives both ops one
 * value socket per value of the addressed event; the native compiler runs
 * value-less events only (graphValidation reports `EVENT_VALUES` otherwise).
 */
export const UNLISTED_BLOCK_DEFS: BlockDef[] = [
    {
        op: GraphOps.EVENT_SEND,
        label: 'Send Event',
        category: 'execution',
        registration: 'babylon-khr',
        docGroup: 'Events',
        description: 'Sends a custom event. Every Receive Event node for the same event runs, in node order, before `out` continues.',
        note: 'Internal events (no id) are private to the graph; delivery is deferred to the next frame in both the native runtime and Babylon\'s KHR loader (the spec leaves timing to the application).',
        flowInputs: FLOW_IN,
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [
            { name: 'event', type: 'number', label: 'Event Index', default: 0, description: 'Index into the graph\'s custom events.' },
        ],
    },
    {
        op: GraphOps.EVENT_RECEIVE,
        label: 'Receive Event',
        category: 'event',
        registration: 'babylon-khr',
        docGroup: 'Events',
        description: 'Fires when its custom event is sent.',
        flowInputs: [],
        flowOutputs: FLOW_OUT,
        dataInputs: [],
        dataOutputs: [],
        configFields: [
            { name: 'event', type: 'number', label: 'Event Index', default: 0, description: 'Index into the graph\'s custom events.' },
        ],
    },
];

// ============================================================================
// Lookup helpers
// ============================================================================

const BLOCK_BY_OP = new Map<string, BlockDef>([...BLOCK_DEFS, ...UNLISTED_BLOCK_DEFS].map(b => [b.op, b]));

/** Look up a block definition by op (palette or unlisted). Returns undefined for unknown ops. */
export function getBlockDef(op: string): BlockDef | undefined {
    return BLOCK_BY_OP.get(op);
}

/** Get all blocks in a category. */
export function getBlocksByCategory(category: BlockCategory): BlockDef[] {
    return BLOCK_DEFS.filter(b => b.category === category);
}

/** Get all blocks in a doc group (e.g. "Math", "Media"). */
export function getBlocksByDocGroup(group: string): BlockDef[] {
    return BLOCK_DEFS.filter(b => b.docGroup === group);
}
